import React from 'react'
import styled from 'styled-components'

import { useRouter } from '@hozana/router'
import { QA } from '@hozana/tests/constants'

import { Button } from 'elements/button/Button'
import { Cell } from 'elements/layout/Cell'
import { Container } from 'elements/layout/Container'
import { Div } from 'elements/layout/Div'
import { FixedDiv } from 'elements/layout/FixedDiv'
import { Row } from 'elements/layout/Row'
import { Space } from 'elements/layout/Space'
import { Link } from 'elements/text/Link'
import { P } from 'elements/text/P'
import { PageTitle } from 'elements/text/PageTitle'
import { Text } from 'elements/text/Text'

import { ICON } from 'config/icons'
import { PAGE } from 'routes/constants'

import { Breadcrumb } from 'modules/guide/components/Breadcrumb'
import { GuideMenuDesktop } from 'modules/guide/components/GuideMenuDesktop'
import { GuideMenuMobile } from 'modules/guide/components/GuideMenuMobile'
import { getGuideTitles } from 'modules/guide/functions'
import type { TGuide } from 'modules/guide/types'

import { GuideContent } from './GuideContent'
import { GuideSourcesSection } from './GuideSourcesSection'

const StyledButton = styled(Button)`
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.3);
`

type TGuideBodyProps = {
  guide: TGuide
  guideName: string
}

export const GuideBody: TFC<TGuideBodyProps> = ({ guide, guideName }) => {
  const { content, parents: rawParents } = guide

  const parents = [...(rawParents || [])].reverse()
  const { locale } = useRouter()

  const titles = content && getGuideTitles(content)

  return (
    <>
      <Breadcrumb {...{ guide, guideName, parents }} />
      <Container maxW="1500px" p={{ xs: '0 1em 1em', sm: '0 50px 60px' }} align="center">
        <PageTitle
          data-testid={QA.GUIDE_PAGE.TITLE}
          color="darkBlue"
          m="0 0 10px"
          fontSize={{ xs: '2em', sm: '3em', md: '4em' }}
          visibleOnMobile
        />
      </Container>
      <Div>
        {titles.length > 1 && (
          <Div
            visible={['xs', 'sm', 'md']}
            w={{ xs: '100%', md: '80%' }}
            m="0 auto 30px"
            maxW="500px"
            p={{ xs: '10px 10px 0', sm: '1em 1em 0', md: '25px 25px 0' }}
          >
            <GuideMenuMobile titles={titles} />
          </Div>
        )}
        <Row>
          {titles.length > 1 && (
            <Cell invisible={['xs', 'sm', 'md']} w="25%" p="25px">
              <GuideMenuDesktop titles={titles} />
            </Cell>
          )}
          <Cell
            w={{ xs: '100%', md: '80%', lg: '75%' }}
            maxW="800px"
            m={{ xs: 'auto', lg: titles.length > 1 ? '0' : 'auto' }}
            p={{ xs: '25px 10px 0', sm: '25px 1em 0', md: '25px 25px 0' }}
          >
            <GuideContent guide={guide} guideName={guideName} />
            <Space h="32px" />
            <GuideSourcesSection sources={guide.sources} />
          </Cell>
        </Row>
        <Div as="footer" p="50px 1em 120px" align="center" bg="background">
          <P fontSize="12px" color="black50">
            Association Hozana - 8 rue du Palais de Justice, 69005 Lyon
          </P>
          <P fontSize="12px" color="black70">
            <Link to={{ pathname: PAGE.CONTACT }} target="_blank" underline>
              <Text>trans:common:words.contact-us</Text>
            </Link>
          </P>
        </Div>
      </Div>
      {!['it', 'pl'].includes(locale) && (
        <FixedDiv zIndex="floatingButton" bottom="70px" right={{ xs: '1em', sm: '20px' }}>
          <StyledButton
            size="big"
            valueLink={{ to: { pathname: PAGE.INTENTIONS }, target: '_blank' }}
            colors="yellow"
            icon={ICON.FIRE}
          >
            trans:guide:intention.page.button
          </StyledButton>
        </FixedDiv>
      )}
    </>
  )
}
